/**
 * LayoutAdaptive — Adaptive Design (JS-driven breakpoints)
 *
 * 3 layout distinti scelti via JavaScript (non CSS fluido):
 *   Mobile  (<768px)     → header + content + bottom tab bar
 *   Tablet  (768-1024px) → sidebar collassata + header + content
 *   Desktop (>1024px)    → sidebar espansa + header + content
 *
 * Usa useBreakpoint() di Ant Design per il rilevamento JS.
 */
import { useState } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import {
  Layout,
  Menu,
  Avatar,
  Dropdown,
  Badge,
  theme,
  Typography,
  Button,
  Grid,
  Drawer,
} from 'antd'
import type { MenuProps } from 'antd'
import {
  UserOutlined,
  BellOutlined,
  LogoutOutlined,
  MenuOutlined,
  SettingOutlined,
  ApiOutlined,
} from '@ant-design/icons'
import { useAuth } from '@/contexts/AuthContext'
import { usePermission } from '@/hooks/usePermission'
import { usePageTitle } from '@/contexts/PageTitleContext'
import { useRegistryMenu, resolveIcon } from '@/hooks/useRegistryMenu'

const { useBreakpoint } = Grid
const { Header, Sider, Content } = Layout
const { Text } = Typography

// Voci che compaiono nella bottom navigation mobile (max 5).
// /profile è incluso perché ha senzaGateVoluto (sempre visibile) → presente in flatVisible.
const BOTTOM_NAV_KEYS = ['/dashboard', '/pre-accettazione', '/schede-lavoro', '/rda', '/profile']

export function LayoutAdaptive() {
  const [drawerOpen, setDrawerOpen] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const { user, logout } = useAuth()
  const { title } = usePageTitle()
  const { token } = theme.useToken()
  const screens = useBreakpoint()
  // 'menu utente' (profile/settings/integrazioni) escluso dal sidebar:
  // profile compare nel bottom nav via flatVisible; settings/integrazioni
  // restano nel dropdown utente (gestiti più in basso con canManageSettings).
  const { items: menuItems, flatVisible } = useRegistryMenu({
    excludeGruppi: ['menu utente'],
  })
  const canManageSettings = usePermission('system.settings')

  // Breakpoint logic (JS-driven, non CSS fluid)
  const isMobile = !screens.md               // <768px
  const isTablet = screens.md && !screens.xl // 768px–1199px

  const userMenuItems: MenuProps['items'] = [
    { key: 'profile', icon: <UserOutlined />, label: 'Profilo', onClick: () => navigate('/profile') },
    { type: 'divider' },
    ...(canManageSettings ? [
      { key: 'settings', icon: <SettingOutlined />, label: 'Impostazioni', onClick: () => navigate('/settings') },
      { key: 'integrazioni', icon: <ApiOutlined />, label: 'Integrazioni', onClick: () => navigate('/integrazioni') },
    ] : []),
    { key: 'logout', icon: <LogoutOutlined />, label: 'Logout', onClick: logout },
  ]

  const bottomNavItems = flatVisible
    .filter(e => BOTTOM_NAV_KEYS.includes(e.path))
    .map(e => ({
      key:   e.path,
      icon:  resolveIcon(e.icon),
      label: e.label,
    }))

  // ── MOBILE layout ─────────────────────────────────────────────────────
  if (isMobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
        {/* Header compatto */}
        <div
          style={{
            height: 56,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '0 16px',
            background: token.colorBgContainer,
            borderBottom: `1px solid ${token.colorBorder}`,
            flexShrink: 0,
          }}
        >
          <Button type="text" icon={<MenuOutlined />} onClick={() => setDrawerOpen(true)} style={{ fontSize: 20 }} />
          <Text style={{ fontWeight: 600, fontSize: 16, flex: 1, textAlign: 'center' }}>
            {title || 'Garage'}
          </Text>
          <Badge count={0} showZero={false}>
            <BellOutlined style={{ fontSize: 20, cursor: 'pointer' }} onClick={() => navigate('/notifications')} />
          </Badge>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 16, paddingBottom: 76 }}>
          <Outlet />
        </div>

        {/* Bottom tab bar */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-around',
            height: 60,
            background: token.colorBgContainer,
            borderTop: `1px solid ${token.colorBorder}`,
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 200,
            paddingBottom: 'env(safe-area-inset-bottom, 0)',
          }}
        >
          {bottomNavItems.map((item: any) => {
            const active = location.pathname === item.key
            return (
              <button
                key={item.key}
                onClick={() => navigate(item.key)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 1,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: active ? token.colorPrimary : token.colorTextSecondary,
                  fontWeight: active ? 600 : 400,
                  gap: 2,
                  minHeight: 48,
                }}
              >
                <span style={{ fontSize: 20 }}>{item.icon}</span>
                <span style={{ fontSize: 10 }}>{item.label}</span>
              </button>
            )
          })}
        </div>

        {/* Menu drawer */}
        <Drawer
          title={<img src="/Tiber-Garage.png" alt="Garage" style={{ height: 32 }} />}
          placement="left"
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          width={240}
          styles={{ body: { padding: 0 } }}
        >
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            items={menuItems}
            onClick={({ key }) => { navigate(key); setDrawerOpen(false) }}
            style={{ borderRight: 0 }}
          />
        </Drawer>
      </div>
    )
  }

  // ── TABLET layout ─────────────────────────────────────────────────────
  if (isTablet) {
    return (
      <Layout style={{ minHeight: '100vh' }}>
        <Sider
          collapsed
          collapsedWidth={56}
          theme="light"
          style={{ borderRight: `1px solid ${token.colorBorder}` }}
        >
          <div
            style={{
              height: 64,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderBottom: `1px solid ${token.colorBorder}`,
            }}
          >
            <img src="/Tiber-Garage.png" alt="Garage" style={{ height: 36, objectFit: 'contain' }} />
          </div>
          <Menu
            mode="inline"
            selectedKeys={[location.pathname]}
            items={menuItems}
            onClick={({ key }) => navigate(key)}
            inlineCollapsed
            style={{ borderRight: 0 }}
          />
        </Sider>
        <Layout>
          <Header
            style={{
              padding: '0 20px',
              background: token.colorBgContainer,
              borderBottom: `1px solid ${token.colorBorder}`,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Text style={{ fontSize: 16, fontWeight: 600 }}>{title}</Text>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Badge count={0} showZero={false}>
                <BellOutlined style={{ fontSize: 18, cursor: 'pointer' }} onClick={() => navigate('/notifications')} />
              </Badge>
              <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
                <Avatar icon={<UserOutlined />} style={{ cursor: 'pointer' }} />
              </Dropdown>
            </div>
          </Header>
          <Content style={{ margin: 16 }}>
            <Outlet />
          </Content>
        </Layout>
      </Layout>
    )
  }

  // ── DESKTOP layout ────────────────────────────────────────────────────
  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        collapsible
        theme="light"
        style={{ borderRight: `1px solid ${token.colorBorder}` }}
      >
        <div
          style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderBottom: `1px solid ${token.colorBorder}`,
          }}
        >
          <img src="/Tiber-Garage.png" alt="Garage" style={{ height: 48, objectFit: 'contain' }} />
        </div>
        <Menu
          mode="inline"
          selectedKeys={[location.pathname]}
          items={menuItems}
          onClick={({ key }) => navigate(key)}
          style={{ borderRight: 0 }}
        />
      </Sider>
      <Layout>
        <Header
          style={{
            padding: '0 24px',
            background: token.colorBgContainer,
            borderBottom: `1px solid ${token.colorBorder}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Text style={{ fontSize: 18, fontWeight: 600 }}>{title}</Text>
          <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
            <Badge count={0} showZero={false}>
              <BellOutlined style={{ fontSize: 20, cursor: 'pointer' }} onClick={() => navigate('/notifications')} />
            </Badge>
            {canManageSettings && (
              <SettingOutlined
                style={{ fontSize: 20, cursor: 'pointer', color: token.colorPrimary }}
                onClick={() => navigate('/settings')}
              />
            )}
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <Avatar icon={<UserOutlined />} />
                <Text style={{ marginLeft: 8 }}>{user?.nome} {user?.cognome}</Text>
              </div>
            </Dropdown>
          </div>
        </Header>
        <Content style={{ margin: 24 }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  )
}

export default LayoutAdaptive
